import * as React from 'react';
import { cn } from '../../utils';

/**
 * Tactile pill used in the hero banner chip row. Apply to an <a>, a router
 * <Link>, or a <span> for non-navigating chips.
 */
export const heroBannerChipClass =
  'rounded-full bg-gradient-to-b from-white to-gray-50 px-5 py-2 text-sm font-semibold text-[#cf2e2e] border border-rose-200 border-b-[4px] border-b-rose-300 shadow-[inset_0_1px_0_rgba(255,255,255,0.8),0_4px_0_rgba(0,0,0,0.08),0_6px_12px_rgba(247,141,167,0.2)] transition-all duration-100 ease-out hover:translate-y-[2px] hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.8),0_2px_0_rgba(0,0,0,0.08),0_3px_6px_rgba(247,141,167,0.15)] active:translate-y-[3px] active:shadow-[inset_0_1px_0_rgba(255,255,255,0.6),0_1px_0_rgba(0,0,0,0.05),0_1px_3px_rgba(247,141,167,0.1)]';

export interface HeroBannerProps {
  /** Decorative mark next to the title, e.g. the brand hand icon <img>. */
  icon?: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Chip row under the title — style each chip with `heroBannerChipClass`. */
  chips?: React.ReactNode;
  /** Right column, typically <HeroCards>. */
  children?: React.ReactNode;
  className?: string;
}

/**
 * Standard dashboard hero: brand gradient banner with greeting/chips on the
 * left and a stat-card grid (HeroCards) on the right. Shared by the student
 * and teacher dashboards so they stay visually in sync.
 */
export function HeroBanner({ icon, title, subtitle, chips, children, className }: HeroBannerProps) {
  return (
    <section
      className={cn(
        'relative w-full rounded-[32px] bg-gradient-to-br from-[#cf2e2e] via-[#f05f7a] to-[#f78da7] text-white',
        // "Palco elevado": layered colored shadow so the banner levitates off the page.
        'shadow-[0_2px_4px_rgba(120,20,40,0.12),0_14px_28px_-8px_rgba(160,30,60,0.28),0_36px_64px_-18px_rgba(240,95,122,0.32)]',
        className,
      )}
    >
      {/* Diffuse glow pooled beneath the banner. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-[7%] -bottom-6 -z-10 h-10 rounded-full bg-[rgba(240,95,122,0.3)] blur-2xl"
      />
      {/* Inner light: soft highlight in the top-left corner, faint echo bottom-right. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-[32px] bg-[radial-gradient(560px_260px_at_12%_-10%,rgba(255,255,255,0.28),transparent_62%),radial-gradient(400px_220px_at_96%_110%,rgba(255,255,255,0.14),transparent_60%)]"
      />
      <div className="relative grid w-full grid-cols-1 gap-6 p-6 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:items-center lg:gap-8 lg:px-10 lg:py-7">
        <div className="min-w-0 space-y-4">
          <div className="flex items-center gap-3">
            {icon}
            <div className="space-y-0.5">
              <h1 className="text-2xl font-semibold leading-tight sm:text-3xl">{title}</h1>
              {subtitle ? <p className="text-sm text-white/85 sm:text-base">{subtitle}</p> : null}
            </div>
          </div>
          {chips ? <div className="flex flex-wrap gap-2.5">{chips}</div> : null}
        </div>

        {children}
      </div>
    </section>
  );
}
